import { Injectable } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import { tap } from 'rxjs/operators'
import {
  IdealTypeQuizSurveyListResponse,
  SurveyCommentPaginatedListResponse,
  SurveyCommentResponse,
  SurveyDetailResponse,
  SurveyPaginatedListResponse,
  SurveyResultResponse,
} from 'src/app/interfaces/survey'
import { UserService } from 'src/app/services/user/user.service'
import { environment } from 'src/environments/environment'
import { MBTI } from '../../interfaces/mbti'
import { SurveyComment } from './../../interfaces/survey'
import { CustomHttpClient } from './../http/http.service'

export type SurveyCommentSubjectParams = {
  type: 'create' | 'update' | 'delete' | 'report' | 'like'
  comment: { id: number } & Partial<SurveyComment>
}

@Injectable({
  providedIn: 'root',
})
export class SurveyService {
  constructor(private http: CustomHttpClient, private userService: UserService) {}

  private _isSurveyVisibleOnHome = true
  private isSurveyVisibleOnHomeSubject = new Subject<boolean>()
  private surveyChangeSubject = new Subject<void>()
  private surveyCommentSubject = new Subject<SurveyCommentSubjectParams>()

  getSurveyList(
    categoryId: number | null,
    cursor: string | null,
    pageSize: number = 5,
    ordering: 'total_answered_count' | null
  ): Observable<SurveyPaginatedListResponse> {
    const queryParams = { cursor, categoryId, pageSize, ordering }
    return this.http.get<SurveyPaginatedListResponse>(`${environment.apiUrl}/v1/communities/surveys`, { queryParams })
  }

  get isSurveyVisibleOnHome(): boolean {
    return this._isSurveyVisibleOnHome
  }

  getIsSurveyVisibleOnHomeSubject(): Observable<boolean> {
    return this.isSurveyVisibleOnHomeSubject.asObservable()
  }

  getSurveyCommentSubject(): Observable<SurveyCommentSubjectParams> {
    return this.surveyCommentSubject.asObservable()
  }

  isSurveyVisibleOnHomeChanged(value: boolean): void {
    this._isSurveyVisibleOnHome = value
    this.isSurveyVisibleOnHomeSubject.next(value)
  }

  getSurveyChangedSubject(): Observable<void> {
    return this.surveyChangeSubject.asObservable()
  }

  surveyChanged(): void {
    this.surveyChangeSubject.next()
  }

  getTodaySurvey(): Observable<SurveyDetailResponse> {
    return this.http.get<SurveyDetailResponse>(`${environment.apiUrl}/v1/communities/surveys/today`)
  }

  getSurveyResult(id: number): Observable<SurveyResultResponse> {
    return this.http.get<SurveyResultResponse>(`${environment.apiUrl}/v1/communities/surveys/${id}`)
  }

  postSurveyAnswer(surveyId: number, optionId: number): Observable<void> {
    const body = { option: optionId }
    return this.http
      .post<void>(`${environment.apiUrl}/v1/communities/surveys/${surveyId}/answers`, body)
      .pipe(tap(() => this.userService.getSurveyProperty()))
  }

  /** 댓글 생성하기 */
  createComment(surveyId: number, content: string): Observable<SurveyCommentResponse> {
    return this.http.post<SurveyCommentResponse>(`${environment.apiUrl}/v1/communities/surveys/${surveyId}/comments`, {
      content,
    })
  }

  /** 댓글 리스트 가져오기 */
  getCommentList(
    surveyId: number,
    cursor: string | null = null,
    pageSize: number = 10
  ): Observable<SurveyCommentPaginatedListResponse> {
    const queryParams = { cursor, pageSize }
    return this.http.get<SurveyCommentPaginatedListResponse>(
      `${environment.apiUrl}/v1/communities/surveys/${surveyId}/comments`,
      { queryParams }
    )
  }

  /** 댓글 업데이트하기 */
  updateComment(commentId: number, content: string): Observable<SurveyCommentResponse> {
    return this.http
      .patch<SurveyCommentResponse>(`${environment.apiUrl}/v1/communities/survey/comments/${commentId}`, { content })
      .pipe(
        tap((res) => {
          this.surveyCommentSubject.next({
            type: 'update',
            comment: res.data,
          })
        })
      )
  }

  /** 댓글 삭제하기 */
  deleteComment(commentId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/v1/communities/survey/comments/${commentId}`).pipe(
      tap((res) => {
        this.surveyCommentSubject.next({
          type: 'delete',
          comment: { id: commentId },
        })
      })
    )
  }

  /** 댓글 신고하기 */
  reportComment(commentId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/v1/communities/survey/comments/${commentId}/report`).pipe(
      tap((res) => {
        this.surveyCommentSubject.next({
          type: 'delete',
          comment: { id: commentId },
        })
      })
    )
  }

  /** 댓글 좋아요 */
  likeComment(commentId: number, isLikeClicked: boolean): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/v1/communities/survey/comments/${commentId}/like`).pipe(
      tap((res) => {
        this.surveyCommentSubject.next({
          type: 'like',
          comment: { id: commentId, isLikeClicked: !isLikeClicked },
        })
      })
    )
  }

  getBestSurveyList(
    type: 'similar' | 'opposite',
    targetMbti: MBTI,
    compareMbti: MBTI | null
  ): Observable<IdealTypeQuizSurveyListResponse> {
    const queryParams = { type, targetMbti, compareMbti }
    return this.http.get<IdealTypeQuizSurveyListResponse>(`${environment.apiUrl}/v1/communities/surveys/best`, {
      queryParams,
    })
  }
}
